import type { BundleAdminDetailToken, BundleAdminProductToken, BundleAdminRowToken, BundleAdminSaveRequest } from '@sleed/bizcommerce-api';

/** `ProductType.Bundle` / `ProductType.Giftbox` — the two product types whose contents the Bundles page manages. */
export const BUNDLE_TYPE = 3;
export const GIFTBOX_TYPE = 4;

/** Route of the Bundles list; the detail page is `${BUNDLES_PATH}/:id` (id = the bundle product id). */
export const BUNDLES_PATH = '/products/bundles';

/** A contents row as edited on the bundle detail page. `key` is client-only (React key; survives id = 0 rows). */
export interface BundleRowDraft {
	key: string;
	id: number;
	cid: number | null;
	child: BundleAdminProductToken | null;
	qty: number;
	prc: number | null;
	prcbd: number | null;
	rprc: number | null;
	dsc: string;
	si: number;
	iv: boolean;
	/** Server warnings from the last load/save; cleared when the row's product changes (they described the old one). */
	wrn: string[];
}

export interface BundleDraft {
	afrom: string;
	ato: string;
	rows: BundleRowDraft[];
}

export interface BundleTotals {
	regular: number;
	bundle: number;
	saving: number;
	savingPct: number;
	/** Visible rows with a product whose live price is not known yet (newly picked) — totals update after save. */
	pending: number;
}

const round2 = (n: number) => Math.round(n * 100) / 100;

let keySeq = 0;
export const newRowKey = () => `r${++keySeq}`;

/** ISO date-time (with or without zone) → `<input type="datetime-local">` value in the viewer's zone. */
export function isoToLocalInput(raw?: string | { toString(): string } | null): string {
	if (!raw) return '';
	const s = String(raw);
	const d = new Date(/[zZ]|[+-]\d{2}:?\d{2}$/.test(s) ? s : `${s}Z`);
	if (Number.isNaN(d.getTime())) return '';
	const pad = (n: number) => String(n).padStart(2, '0');
	return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** `<input type="datetime-local">` value → ISO string for the API (null when empty/invalid). */
export function localInputToIso(value: string): string | null {
	if (!value) return null;
	const d = new Date(value);
	return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

export function rowToDraft(r: BundleAdminRowToken): BundleRowDraft {
	return {
		key: newRowKey(),
		id: r.id ?? 0,
		cid: r.cid ?? null,
		child: r.child ?? null,
		qty: r.qty ?? 1,
		prc: r.prc ?? null,
		prcbd: r.prcbd ?? null,
		rprc: r.rprc ?? null,
		dsc: r.dsc ?? '',
		si: r.si ?? 0,
		iv: r.iv ?? true,
		wrn: r.wrn ?? []
	};
}

export function detailToDraft(d: BundleAdminDetailToken): BundleDraft {
	return { afrom: isoToLocalInput(d.afrom), ato: isoToLocalInput(d.ato), rows: (d.rows ?? []).map(rowToDraft) };
}

export function emptyRow(rows: BundleRowDraft[]): BundleRowDraft {
	const nextSort = rows.reduce((max, r) => Math.max(max, r.si), 0) + 1;
	return {
		key: newRowKey(),
		id: 0,
		cid: null,
		child: null,
		qty: 1,
		prc: null,
		prcbd: null,
		rprc: null,
		dsc: '',
		si: nextSort,
		iv: true,
		wrn: []
	};
}

/** The whole contents in one request — rows left out are deleted server-side. */
export function draftToSaveRequest(bundleId: number, draft: BundleDraft): BundleAdminSaveRequest {
	return {
		bid: bundleId,
		afrom: localInputToIso(draft.afrom),
		ato: localInputToIso(draft.ato),
		rows: draft.rows.map(r => ({
			id: r.id,
			cid: r.cid,
			qty: r.qty,
			prc: r.prc,
			prcbd: r.prcbd,
			rprc: r.rprc,
			dsc: r.dsc.trim() ? r.dsc : null,
			si: r.si,
			iv: r.iv
		}))
	};
}

/** Draft content without the client-only keys/warnings — for the dirty check. */
export function draftSignature(draft: BundleDraft): string {
	return JSON.stringify(draftToSaveRequest(0, draft));
}

/** Live price of the row's product, or null when unknown (newly picked, not yet saved). */
export function rowLivePrice(row: BundleRowDraft): number | null {
	return row.child?.prc ?? null;
}

/** Per-row read-only figures (bundle mode): unit saving and line total; null when the live price is unknown. */
export function rowFigures(row: BundleRowDraft): { unitSaving: number; lineTotal: number } | null {
	const live = rowLivePrice(row);
	if (live == null || row.prc == null) return null;
	const unit = Math.min(live, row.prc);
	return { unitSaving: round2(live - unit), lineTotal: round2(unit * row.qty) };
}

/**
 * Bundle totals recomputed client-side as rows change: per visible row with a product and a known live price,
 * `min(live, prc) × qty` against `live × qty` (a row without a bundle price counts at the live price). Rows whose
 * live price isn't known yet are counted in `pending` and left out — the server figures replace these after save.
 */
export function computeBundleTotals(rows: BundleRowDraft[]): BundleTotals {
	let regular = 0;
	let saving = 0;
	let pending = 0;
	for (const row of rows) {
		if (!row.iv || row.cid == null) continue;
		const live = rowLivePrice(row);
		if (live == null) {
			pending++;
			continue;
		}
		// Like the server (BundlePricing.Summarize): rows without a bundle price don't count
		if (row.prc == null) continue;
		const qty = Math.max(0, row.qty || 0);
		regular += live * qty;
		saving += (live - Math.min(live, row.prc)) * qty;
	}
	regular = round2(regular);
	saving = round2(saving);
	const bundle = round2(regular - saving);
	const savingPct = regular > 0 ? Math.round((saving / regular) * 1000) / 10 : 0;
	return { regular, bundle, saving, savingPct, pending };
}

/**
 * Row warnings to show: the server's (from the last load/save), with the two that depend only on the edited
 * values (NoSaving, ChildInTwoRows) recomputed live for bundles so they follow the edits. Giftboxes show the
 * server's as-is.
 */
export function rowWarnings(row: BundleRowDraft, rows: BundleRowDraft[], isBundle: boolean): string[] {
	if (!isBundle) return row.wrn;
	const out = row.wrn.filter(w => w !== 'NoSaving' && w !== 'ChildInTwoRows');
	if (row.cid != null && rows.some(r => r !== row && r.cid === row.cid)) out.push('ChildInTwoRows');
	const live = rowLivePrice(row);
	if (live != null && row.prc != null && row.prc >= live) out.push('NoSaving');
	return out;
}
